import { apiClient } from './api'
// Types defined inline
export type Role = {
  id: string
  name: string
  description?: string
  is_system?: boolean
  target_org_type?: string | null
  permissions?: Permission[]
  user_count?: number
}

export type Permission = {
  id: string
  name: string
  resource: string
  action: string
  description?: string
}

export type CreateRoleRequest = {
  name: string
  description?: string
  permission_ids?: string[]
}

export type UpdateRoleRequest = {
  name?: string
  description?: string
  permission_ids?: string[]
}

export type CreatePermissionRequest = {
  name: string
  resource: string
  action: string
  description?: string
}

export type UpdatePermissionRequest = {
  name?: string
  resource?: string
  action?: string
  description?: string
}

export type UserRoleAssignment = {
  user_id: string
  role_id: string
}

export type RBACSystemInfo = {
  total_roles: number
  total_permissions: number
  total_user_assignments: number
  system_roles_count: number
  custom_roles_count: number
}

export type ResourcePermissions = {
  [resource: string]: string[]
}

export const rbacApi = {
  // =============================================================================
  // ROLES API
  // =============================================================================

  // Get all roles
  getRoles: (params?: {
    skip?: number
    limit?: number
    include_system?: boolean
    organization_id?: string
  }): Promise<Role[]> => {
    const queryParams = new URLSearchParams()
    if (params?.skip !== undefined) queryParams.append('skip', params.skip.toString())
    if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString())
    if (params?.include_system !== undefined)
      queryParams.append('include_system', params.include_system.toString())
    if (params?.organization_id) queryParams.append('organization_id', params.organization_id)

    return apiClient.get(`/api/v1/rbac/roles?${queryParams}`)
  },

  // `allOrgs` é o opt-in explícito de visão global, honrado só p/ super admin: o portal
  // adm administra roles de qualquer org sem trocar de contexto. Sem ele, o backend
  // escopa à org ativa — ver backend/app/api/v1/rbac.py.

  // Get specific role
  getRole: (roleId: string, allOrgs = false): Promise<Role> => {
    return apiClient.get(`/api/v1/rbac/roles/${roleId}${allOrgs ? '?all_orgs=true' : ''}`)
  },

  // Create new role
  createRole: (roleData: CreateRoleRequest): Promise<Role> => {
    return apiClient.post('/api/v1/rbac/roles', roleData)
  },

  // Update role
  updateRole: (roleId: string, roleData: UpdateRoleRequest, allOrgs = false): Promise<Role> => {
    return apiClient.put(`/api/v1/rbac/roles/${roleId}${allOrgs ? '?all_orgs=true' : ''}`, roleData)
  },

  // Delete role
  deleteRole: (roleId: string, allOrgs = false): Promise<{ message: string }> => {
    return apiClient.delete(`/api/v1/rbac/roles/${roleId}${allOrgs ? '?all_orgs=true' : ''}`)
  },

  // Update role permissions only
  updateRolePermissions: (
    roleId: string,
    permissionIds: string[],
    allOrgs = false,
  ): Promise<Role> => {
    return apiClient.put(`/api/v1/rbac/roles/${roleId}${allOrgs ? '?all_orgs=true' : ''}`, {
      permission_ids: permissionIds,
    })
  },

  // =============================================================================
  // PERMISSIONS API
  // =============================================================================

  // Get all permissions
  getPermissions: (params?: {
    skip?: number
    limit?: number
    resource?: string
    action?: string
  }): Promise<Permission[]> => {
    const queryParams = new URLSearchParams()
    if (params?.skip !== undefined) queryParams.append('skip', params.skip.toString())
    if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString())
    if (params?.resource) queryParams.append('resource', params.resource)
    if (params?.action) queryParams.append('action', params.action)

    return apiClient.get(`/api/v1/rbac/permissions?${queryParams}`)
  },

  // Get specific permission
  getPermission: (permissionId: string): Promise<Permission> => {
    return apiClient.get(`/api/v1/rbac/permissions/${permissionId}`)
  },

  // Create new permission
  createPermission: (permissionData: CreatePermissionRequest): Promise<Permission> => {
    return apiClient.post('/api/v1/rbac/permissions', permissionData)
  },

  // Update permission
  updatePermission: (
    permissionId: string,
    permissionData: UpdatePermissionRequest,
  ): Promise<Permission> => {
    return apiClient.put(`/api/v1/rbac/permissions/${permissionId}`, permissionData)
  },

  // Delete permission
  deletePermission: (permissionId: string): Promise<{ message: string }> => {
    return apiClient.delete(`/api/v1/rbac/permissions/${permissionId}`)
  },

  // =============================================================================
  // USER ROLE ASSIGNMENT API
  // =============================================================================

  // Assign role to user
  assignRoleToUser: (
    userId: string,
    roleData: { role_id: string },
  ): Promise<UserRoleAssignment> => {
    return apiClient.post(`/api/v1/rbac/users/${userId}/roles`, {
      user_id: userId,
      role_id: roleData.role_id,
    })
  },

  // Remove role from user
  removeRoleFromUser: (userId: string, roleId: string): Promise<{ message: string }> => {
    return apiClient.delete(`/api/v1/rbac/users/${userId}/roles/${roleId}`)
  },

  // Get user roles (legacy global)
  getUserRoles: (userId: string): Promise<Role[]> => {
    return apiClient.get(`/api/v1/rbac/users/${userId}/roles`)
  },

  // Get roles for a user in a specific organization (multi-org)
  getMembershipRoles: (userId: string, orgId: string): Promise<Role[]> => {
    return apiClient.get(`/api/v1/rbac/users/${userId}/memberships/${orgId}/roles`)
  },

  // Set roles for a user in a specific organization (replaces all)
  setMembershipRoles: (userId: string, orgId: string, roleIds: string[]): Promise<Role[]> => {
    return apiClient.put(`/api/v1/rbac/users/${userId}/memberships/${orgId}/roles`, {
      role_ids: roleIds,
    })
  },

  // =============================================================================
  // SYSTEM INFO API
  // =============================================================================

  // Get RBAC system information
  getSystemInfo: (): Promise<RBACSystemInfo> => {
    return apiClient.get('/api/v1/rbac/system-info')
  },

  // Get resource permissions
  getResourcePermissions: (): Promise<ResourcePermissions[]> => {
    return apiClient.get('/api/v1/rbac/resources')
  },

  // Get role templates
  getRoleTemplates: (): Promise<any[]> => {
    return apiClient.get('/api/v1/rbac/role-templates')
  },

  // =============================================================================
  // UTILITY FUNCTIONS
  // =============================================================================

  // Get available resources (unique list)
  getAvailableResources: async (): Promise<string[]> => {
    const permissions = await rbacApi.getPermissions()
    const resources = [...new Set(permissions.map((p) => p.resource))]
    return resources.sort()
  },

  // Get available actions (unique list)
  getAvailableActions: async (): Promise<string[]> => {
    const permissions = await rbacApi.getPermissions()
    const actions = [...new Set(permissions.map((p) => p.action))]
    return actions.sort()
  },

  // Get permissions by resource
  getPermissionsByResource: async (resource: string): Promise<Permission[]> => {
    return rbacApi.getPermissions({ resource })
  },

  // Get permissions by action
  getPermissionsByAction: async (action: string): Promise<Permission[]> => {
    return rbacApi.getPermissions({ action })
  },

  // Get user assignments
  getUserAssignments: (): Promise<any[]> => {
    return apiClient.get('/api/v1/rbac/user-assignments')
  },
}

export default rbacApi
